import dayjs from 'dayjs';
import { Message } from 'element-ui';
import config from './config.js';

// 格式化查询日期
export const formatSearchTime = searchTime => {
    return {
        createTimeStart: Array.isArray(searchTime) && searchTime.length > 0 ? dayjs(searchTime[0]).format('YYYY-MM-DD HH:mm:ss') : '',
        createTimeEnd: Array.isArray(searchTime) && searchTime.length > 0 ? dayjs(searchTime[1]).format('YYYY-MM-DD HH:mm:ss') : ''
    };
};
/**
 * @description 格式化查询参数
 * @param {Object} data 查询参数
 */
export const formatSearchData = data => {
    const { createTimeStart, createTimeEnd } = formatSearchTime(data.searchTime);
    const { createTimeStart: appointmentTimeStart, createTimeEnd: appointmentTimeEnd } = formatSearchTime(data.appointmentTime);
    if (data.whCode) {
        data.whNameList = data.whName.length ? data.whName.split(',') : [];
        data.whCodeList = data.whCode.length ? data.whCode.split(',') : [];
    }
    data.createTimeStart = createTimeStart;
    data.createTimeEnd = createTimeEnd;
    data.appointmentTimeStart = appointmentTimeStart;
    data.appointmentTimeEnd = appointmentTimeEnd;
    delete data.appointmentTime;
    delete data.startTime;
    delete data.endTime;
    delete data.searchTime;
    delete data.whName;
    delete data.whCode;
    return data;
};

/**
 * @description 校验查询参数
 * @param {Object} data 查询参数
 * @param {String} customerOrderNo 客户订单号
 * @param {String} createTimeStart 创建开始时间
 * @param {String} createTimeEnd 创建结束时间
 * @param {Number} maxDay63 时间间隔 默认两个月
 * @param {Number} sDateTimeStamp 创建时间起始时间戳
 * @param {Number} eDateTimeStamp 创建时间结束时间戳
 *
 * 校验规则：
 * - 客户订单号 和 创建时间 不可同时为空
 * - 按时间查询或导出时，查询或导出时间跨度不可超过两个月
 */
export const validateSearchData = data => {
    if (!data.customerOrderNo && !data.createTimeStart && !data.createTimeEnd) {
        Message.warning('客户订单号 和 创建时间 不可同时为空');
        return false;
    }
    if (data.whCodeList && data.whCodeList.length > 5) {
        Message.warning('仓库最多支持查询5个');
        return false;
    }
    const options = { span: 7, unit: 'day', noMessage: false, addOneDay: false };
    return validateMaxDate(data, 'createTimeStart', 'createTimeEnd', options);
};

// 处理导出参数
export const dealWithExportParams = searchParams => {
    const { createTimeStart, createTimeEnd } = searchParams;
    for (const k in searchParams) {
        if (searchParams[k] === '') {
            delete searchParams[k];
        }
    }
    delete searchParams.createTimeStart;
    delete searchParams.createTimeEnd;
    return {
        ...searchParams,
        beginDate: createTimeStart,
        endDate: createTimeEnd
    };
};

/**
 * @description 组装echart参数
 */
const getEchartOptions = data => {
    return {
        tooltip: {
            trigger: 'axis',
            axisPointer: {
                type: 'shadow'
            }
        },
        legend: {
            itemHeight: 6,
            itemWidth: 10,
            top: 20
        },
        grid: {
            left: '3%',
            right: '4%',
            bottom: '3%',
            containLabel: true
        },
        xAxis: {
            type: 'value',
            boundaryGap: [0, 0.01],
            axisLine: {
                lineStyle: {
                    color: '#999'
                }
            }
        },
        yAxis: {
            type: 'category',
            data: data.labels,
            axisLine: {
                show: false
            },
            axisTick: {
                show: false
            }
        },
        color: ['#42a5fa', 'yellow', '#ff4b6e'],
        series: [
            {
                name: '正常进行中',
                type: 'bar',
                data: data.normalPedding,
                showBackground: true,
                backgroundStyle: {
                    color: 'rgba(180, 180, 180, 0.1)'
                }
            },
            {
                name: '异常未受理',
                type: 'bar',
                data: data.exceptionProcessing
            },
            {
                name: '异常已受理',
                type: 'bar',
                data: data.exceptionFinished
            }
        ]
    };
};

/**
 * @description 处理后端返回的数据成echart格式
 * @param {Array} normalPedding 正常进行中
 * @param {Array} exceptionProcessing 异常未受理
 * @param {Array} exceptionFinished 异常已受理
 */
export const formatTaskSummaryData = array => {
    const normalPedding = [];
    const exceptionProcessing = [];
    const exceptionFinished = [];
    const labels = [];
    array.forEach(v => {
        labels.push(v.taskName);
        normalPedding.push(v.normalNumber);
        exceptionProcessing.push(v.exceptionProcessingNumber);
        exceptionFinished.push(v.exceptionFinishNumber);
    });
    return getEchartOptions({
        labels,
        normalPedding,
        exceptionProcessing,
        exceptionFinished
    });
};

/**
 * @description 调整列配置后回调
 * @param {String} labels 调整后的列字符串
 * @returns Array
 */
export const adjustTableColumn = labels => {
    const labelList = labels.split(',');
    const columnsList = [];
    const initTableColumnsMap = config.table.columns.reduce((obj, curr) => {
        obj[curr.label] = curr;
        return obj;
    }, {});
    labelList.forEach(k => {
        initTableColumnsMap[k] && columnsList.push(initTableColumnsMap[k]);
    });
    return columnsList;
};

/**
 * @description 根据资源数据找到当前对应路由页面所在的资源编码
 * @param {Object} ctx 组件对象上下文
 * @param {Object} node 节点对象
 */
export const findMenuResourceCode = (ctx, node) => {
    let resourceCode = '';
    const deepMenu = node => {
        if (!node.subResources) return;
        node.subResources.forEach(v => {
            if (v.frontUrl === ctx.$route.path) {
                resourceCode = v.resourceCode;
            } else {
                deepMenu(v);
            }
        });
    };
    deepMenu(node);
    return resourceCode;
};

/**
 * @param {Object} _this 当前引用该函数组件上下文
 */
export const getConfigParams = _this => {
    const configName = _this.config.table.columns.map(v => v.prop).join(',');
    const configNameCn = _this.config.table.columns.map(v => v.label).join(',');
    return {
        configName, // 列配置prop字段
        configNameCn, // 列配置label字段
        subjectType: _this.pageID, // 项目类型
        id: _this.user.employeeId // 登录信息employeeId字段
    };
};

/**
 * @description 校验查询时间跨度不能超过指定日期
 * @param {Object} searchData 查询参数
 * @param {String} sDateField 查询起始时间字段名
 * @param {String} eDateField 查询结束时间字段名
 * @param {{ span: number, unit: dayjs.ManipulateType, noMessage: boolean, addOneDay: boolean }} 配置时间范围限制
 */
const validateMaxDate = (searchData, sDateField, eDateField, options) => {
    if (searchData[sDateField] && searchData[eDateField]) {
        if (!options) {
            const sDateTimeStamp = new Date(searchData[sDateField]).getTime();
            const eDateTimeStamp = new Date(searchData[eDateField]).getTime();
            const maxDay = 1000 * 60 * 60 * 24 * 63; // 按31天来算，组件问题，得加多一天
            if (eDateTimeStamp - sDateTimeStamp > maxDay) {
                Message.warning(`按时间查询或导出时，查询或导出时间跨度不可超过两个月`);
                return false;
            }
        } else {
            const { span = 2, unit = 'month', noMessage = false, addOneDay = true } = options || {};
            const sDate = dayjs(searchData[sDateField]);
            const eDate = dayjs(searchData[eDateField]);
            let addedTime = sDate.startOf('day')
                .add(span, unit);
            if (addOneDay) {
                addedTime = addedTime.add(2, 'day'); // 大小月问题，再多加一天
            }
            const eTime = eDate.endOf('day');
            const isValid = addedTime.isAfter(eTime);
            if (!isValid && !noMessage) {
                const unitNames = {
                    day: '天',
                    month: '个月',
                    week: '周',
                    year: '年'
                };
                Message.warning(`按时间查询或导出时，查询或导出时间跨度不可超过${span}${unitNames[unit]}`);
            }
            return isValid;
        }
    }
    return true;
};
